import { Cancel01Icon, Mail01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Stack,
  Box,
  Radio,
  InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";

interface InviteTeamMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: { email: string; role: string }) => Promise<void>;
  isLoading: boolean;
}

const validationSchema = Yup.object({
  email: Yup.string()
    .email("Dirección de correo electrónico no válida")
    .required("El correo electrónico es obligatorio"),
  role: Yup.string()
    .oneOf(["admin", "manager", "rider"], "Rol no válido")
    .required("Debes asignar un rol"),
});

export const InviteTeamMemberDialog: React.FC<InviteTeamMemberDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const formik = useFormik({
    initialValues: {
      email: "",
      role: "manager", // Default to Management as shown in the screenshot
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await onSubmit(values);
        resetForm();
        onClose();
      } catch (error) {
        console.error("Error submitting invitation:", error);
      }
    },
  });

  const handleRoleCardClick = (role: string) => {
    formik.setFieldValue("role", role);
  };

  const roleOptions = [
    {
      value: "admin",
      label: "Administrador",
      description: "Acceso completo a configuraciones, facturación y gestión del equipo.",
    },
    {
      value: "manager",
      label: "Gestión",
      description: "Puede gestionar menús, inventario y turnos del personal.",
    },
    {
      value: "rider",
      label: "Repartidor",
      description: "Acceso restringido únicamente a la aplicación móvil de entregas.",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          padding: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Stack spacing={0.5}>
          <Typography variant="h3" sx={{ fontWeight: 600, color: "#07154E" }}>
            Invitar Miembro del Equipo
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            Envía una invitación para unirse a tu espacio de trabajo.
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="close" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <HugeiconsIcon icon={Cancel01Icon} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          <Stack spacing={3}>
            {/* Email Field */}
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#07154E" }}>
                Dirección de Correo del Miembro
              </Typography>
              <TextField
                fullWidth
                id="email"
                name="email"
                placeholder="ej. nombre@empresa.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <HugeiconsIcon icon={Mail01Icon} size={20} color="#999" />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2.5,
                  },
                }}
              />
            </Stack>

            {/* Role Selection */}
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#07154E" }}>
                Asignar Rol
              </Typography>

              <Stack spacing={2}>
                {roleOptions.map((roleOpt) => {
                  const isSelected = formik.values.role === roleOpt.value;
                  return (
                    <Box
                      key={roleOpt.value}
                      onClick={() => handleRoleCardClick(roleOpt.value)}
                      sx={{
                        border: `1.5px solid ${isSelected ? "#015FEE" : "#E2E8F0"}`,
                        borderRadius: 3,
                        padding: 2.5,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#F0F7FF" : "transparent",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: isSelected ? "#015FEE" : "#CBD5E1",
                          backgroundColor: isSelected ? "#F0F7FF" : "#F8FAFC",
                        },
                      }}
                    >
                      <Radio
                        checked={isSelected}
                        value={roleOpt.value}
                        name="role"
                        onChange={formik.handleChange}
                        sx={{
                          padding: 0,
                          marginTop: "2px",
                          color: "#CBD5E1",
                          "&.Mui-checked": {
                            color: "#015FEE",
                          },
                        }}
                      />
                      <Stack spacing={0.5} sx={{ width: "100%" }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? "#015FEE" : "#07154E",
                          }}
                        >
                          {roleOpt.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                          {roleOpt.description}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, display: "flex", gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: 2.5,
              borderColor: "#E2E8F0",
              color: "#475569",
              paddingY: 1.5,
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F8FAFC",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isLoading}
            sx={{
              borderRadius: 2.5,
              backgroundColor: "#015FEE",
              color: "white",
              paddingY: 1.5,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#0052D4",
              },
            }}
          >
            {isLoading ? "Invitando..." : "Enviar Invitación"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteTeamMemberDialog;
